import { TypeOptions } from "react-toastify/dist/types";

import { getSessionStatus } from "../../../store/root.selectors";
import {
  ADD_NOTIFICATION,
  DISMISS_NOTIFICATION,
  REMOVE_ALL_CONTEXTUAL_NOTIFICATIONS,
  NOTIFICATION_TYPES,
  NotificationActionTypes,
  NotificationOptionsAction,
  AddNotificationAction,
  NotificationContext,
  ADD_SESSION_EXP_NOTIFICATION,
} from "./notification.types";
import { SBThunkAction } from "../../../store/redux.types";
import { NotificatorService } from "./notification";
import { getNotificationList } from "./notification.selectors";
import { CloseButton } from "../hooks/useRemoteReminderBanner";

export const AUTO_CLOSE_TIMEOUT = 3000;

interface ShowArgs {
  id: string;
  type: TypeOptions;
  options?: NotificationOptionsAction;
}

const show =
  ({
    id,
    type,
    options = {},
  }: ShowArgs): SBThunkAction<void, AddNotificationAction> =>
  (dispatch, getState) => {
    const isSessionExp = getSessionStatus(getState());

    if (isSessionExp) return;
    if (NotificatorService.isActive(id)) return;

    const { values, title, context } = options;

    NotificatorService.show({ id, type, values, title });
    dispatch(addNotification({ id, type, ...(context && { context }) }));
  };

const success =
  (
    id: string,
    options?: NotificationOptionsAction
  ): SBThunkAction<void, AddNotificationAction> =>
  (dispatch) => {
    dispatch(show({ id, type: NOTIFICATION_TYPES.SUCCESS, options }));
  };

const info =
  (
    id: string,
    options?: NotificationOptionsAction
  ): SBThunkAction<void, AddNotificationAction> =>
  (dispatch) => {
    dispatch(show({ id, type: NOTIFICATION_TYPES.INFO, options }));
  };

const warning =
  (
    id: string,
    options?: NotificationOptionsAction
  ): SBThunkAction<void, AddNotificationAction> =>
  (dispatch) => {
    dispatch(show({ id, type: NOTIFICATION_TYPES.WARNING, options }));
  };

const error =
  (
    id: string,
    options?: NotificationOptionsAction
  ): SBThunkAction<void, AddNotificationAction> =>
  (dispatch) => {
    dispatch(show({ id, type: NOTIFICATION_TYPES.ERROR, options }));
  };

const addSessionExpirationNotification =
  (): SBThunkAction<void, NotificationActionTypes> => (dispatch) => {
    const id = "session_expired_some_time";
    NotificatorService.dismiss();
    NotificatorService.show({
      id,
      type: NOTIFICATION_TYPES.ERROR,
    });

    dispatch({
      type: ADD_SESSION_EXP_NOTIFICATION,
      payload: { id, type: NOTIFICATION_TYPES.ERROR },
    });
  };

const addRemoteInfoNotification =
  (): SBThunkAction<void, NotificationActionTypes> => (dispatch) => {
    const id = "remote_reminder_toast";
    const type = "info";

    if (NotificatorService.isActive(id)) return;

    NotificatorService.showPermanent({ id, type, CloseButton: CloseButton });
    dispatch(
      addNotification({ id, type, context: NotificationContext.Contextual })
    );
  };

interface addNotificationArgs {
  id: string;
  type: TypeOptions;
  context?: NotificationContext;
}

export const addNotification = (
  payload: addNotificationArgs
): AddNotificationAction => ({
  type: ADD_NOTIFICATION,
  payload,
});

const remove =
  (id: string): SBThunkAction<void, NotificationActionTypes> =>
  (dispatch) => {
    NotificatorService.dismiss(id);

    dispatch({
      type: DISMISS_NOTIFICATION,
      payload: id,
    });
  };

const removeAllContextual =
  (): SBThunkAction<void, NotificationActionTypes> => (dispatch, getState) => {
    const notifications = getNotificationList(getState());
    const notificationsToRemove = notifications.filter(
      (notification) => notification.context === NotificationContext.Contextual
    );

    notificationsToRemove.forEach((notification) =>
      NotificatorService.dismiss(notification.id)
    );

    dispatch({
      type: REMOVE_ALL_CONTEXTUAL_NOTIFICATIONS,
    });
  };

export const Notificator = {
  success,
  info,
  warning,
  error,
  sessionExpiration: addSessionExpirationNotification,
  remoteInfo: addRemoteInfoNotification,
  remove,
  removeAllContextual,
};
